import CloseIcon from "@mui/icons-material/Close";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CommonDashboardBox } from "../../../components/Dashboard/CommonDashboardBox";
import { CommonDashboardHeader } from "../../../components/Dashboard/CommonDashboardHeader";
import { JumpingDots } from "../../../components/UIToolkit/Animations/JumpingDots";
import { type FlatSwitchOption, SystemFlatSwitch } from "../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { InHousePerformanceHeatmap } from "../../../components/UIToolkit/Graphs/PerformanceHeatmap/InHousePerformanceHeatmap";
import type { PerformanceHeatmapRow } from "../../../components/UIToolkit/Graphs/PerformanceHeatmap/types";
import { SystemDropdown } from "../../../components/UIToolkit/Inputs/SystemDropDown";
import type { DropdownItem } from "../../../components/UIToolkit/Inputs/SystemDropDown/types";
import { EmptyState } from "../../../components/UIToolkit/molecules/EmptyState";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { MultiFacilityPerformanceFacility, MultiFacilityPerformanceOverTimeResponse, MultiFacilityPerformanceValue } from "../../../utils/models";
import {
	type PerformanceDisplayMode,
	type PerformanceMetric,
	type PerformanceSort,
	performanceAverageRow,
	performanceCellTone,
	performanceDistanceFromTarget,
	performanceValuesFor,
	performanceVariance,
	sortPerformanceFacilities,
} from "./utils";

type Props = {
	data?: MultiFacilityPerformanceOverTimeResponse;
	isLoading: boolean;
	isError: boolean;
	isActual: boolean;
	ppdAllowed: boolean;
	selectedDepartmentIds: string[];
	currentDepartmentId: string;
	/** Drops one facility from the page-level selection (the row label's own remove control). */
	onRemoveFacility: (departmentId: string) => void;
	/**
	 * Facilities whose wage data this user may not see. Their PPD cells arrive zeroed from the backend,
	 * so they are excluded from the network average rather than dragging it toward zero.
	 */
	wageRestrictedDepartmentIds: ReadonlySet<string>;
};

const selectedPerformanceFacilities = (data: MultiFacilityPerformanceOverTimeResponse, selectedDepartmentIds: string[]): MultiFacilityPerformanceFacility[] => {
	const selected = new Set(selectedDepartmentIds);
	return data.facilities.filter((facility) => selected.has(facility.departmentId));
};

const formatPerformanceNumber = (value: number, metric: PerformanceMetric, language: string): string => {
	if (metric === "ppd") {
		return new Intl.NumberFormat(language, { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
	}
	return new Intl.NumberFormat(language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
};

const formatPerformanceVariance = (value: number, metric: PerformanceMetric, language: string): string => {
	const sign = value > 0 ? "+" : "";
	return `${sign}${formatPerformanceNumber(value, metric, language)}`;
};

const formatPerformanceDistance = (value: number, language: string): string =>
	new Intl.NumberFormat(language, { style: "percent", minimumFractionDigits: 0, maximumFractionDigits: 1 }).format(value);

const formatWeekRange = (startDate: string, endDate: string, language: string): string => {
	const start = DateTime.fromISO(startDate).setLocale(language);
	const end = DateTime.fromISO(endDate).setLocale(language);
	return `${start.toFormat("LLL d")}–${end.toFormat("LLL d")}`;
};

/** Row id of the pinned network-average row; never a real department id. */
const AVERAGE_ROW_ID = "__network_average__";

const displayCellValue = (value: MultiFacilityPerformanceValue, displayMode: PerformanceDisplayMode): number | null =>
	displayMode === "value" ? value.actual : performanceVariance(value);

/** Full-width portfolio performance card; values remain backend-owned census-weighted pairs. */
export const MultiFacilityPerformanceHeatmap: FC<Props> = ({
	data,
	isLoading,
	isError,
	isActual,
	ppdAllowed,
	selectedDepartmentIds,
	currentDepartmentId,
	onRemoveFacility,
	wageRestrictedDepartmentIds,
}) => {
	const { t, i18n } = useTranslation();
	const { track } = useAnalytics();
	const [metric, setMetric] = useState<PerformanceMetric>("hppd");
	const [displayMode, setDisplayMode] = useState<PerformanceDisplayMode>("value");
	// Opens on the biggest favorable variance: the network's best-performing facilities lead, which is
	// what the matrix is scanned for. "Current facility, then A-Z" remains one dropdown click away.
	const [sort, setSort] = useState<PerformanceSort>("favorable");
	const ppdAvailable = ppdAllowed && data?.ppdAvailable === true;
	// Derive the rendered metric synchronously: when PPD becomes unavailable the backend returns an
	// empty `ppd` array, so reading `metric === "ppd"` for rows/sorting during the same render (before
	// the reset effect below runs) would index that empty array and throw, blanking the route.
	const effectiveMetric: PerformanceMetric = ppdAvailable ? metric : "hppd";

	useEffect(() => {
		if (!ppdAvailable && metric === "ppd") setMetric("hppd");
	}, [metric, ppdAvailable]);

	const metricOptions = useMemo<FlatSwitchOption<PerformanceMetric>[]>(
		() => [
			{ label: t("mc.multi.performance.metric.hppd"), value: "hppd", "data-testid": "mc-multi-performance-metric-hppd" },
			...(ppdAvailable ? [{ label: t("mc.multi.performance.metric.ppd"), value: "ppd" as const, "data-testid": "mc-multi-performance-metric-ppd" }] : []),
		],
		[ppdAvailable, t],
	);
	const displayOptions = useMemo<FlatSwitchOption<PerformanceDisplayMode>[]>(
		() => [
			{ label: t("mc.multi.performance.value"), value: "value", "data-testid": "mc-multi-performance-display-value" },
			{ label: t("mc.multi.performance.difference"), value: "difference", "data-testid": "mc-multi-performance-display-difference" },
		],
		[t],
	);
	const sortItems = useMemo<DropdownItem<PerformanceSort>[]>(
		() => [
			{ value: "current", label: t("mc.multi.performance.sort.current") },
			{ value: "unfavorable", label: t("mc.multi.performance.sort.unfavorable") },
			{ value: "favorable", label: t("mc.multi.performance.sort.favorable") },
		],
		[t],
	);
	const facilities = useMemo(() => {
		if (data === undefined) return [];
		return sortPerformanceFacilities({
			facilities: selectedPerformanceFacilities(data, selectedDepartmentIds),
			metric: effectiveMetric,
			sort,
			currentDepartmentId,
		});
	}, [currentDepartmentId, data, effectiveMetric, selectedDepartmentIds, sort]);
	const heatmapRows = useMemo<PerformanceHeatmapRow[]>(() => {
		if (data === undefined) return [];
		return facilities.map((facility) => {
			const values = performanceValuesFor(facility, effectiveMetric);
			return {
				id: facility.departmentId,
				// The row label carries its own remove control, so a facility can be dropped from the
				// comparison here as well as from the table and the Facilities dropdown. It reveals on
				// row hover/focus to keep the resting label uncluttered, and stays keyboard-reachable.
				// Every element here must be PHRASING content: the generic heatmap wraps `label` in a
				// Typography, which renders a <p>, and a <div> inside a <p> is invalid HTML that React
				// reports as a hydration error. Hence spans with inline-flex rather than plain Boxes.
				label: (
					<Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
						<Box
							component="span"
							sx={{ fontWeight: facility.departmentId === currentDepartmentId ? 700 : 500, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}
						>
							{facility.departmentName}
						</Box>
						<Tooltip title={t("mc.multi.grid.remove.facility", { facility: facility.departmentName })}>
							<IconButton
								size="small"
								onClick={() => onRemoveFacility(facility.departmentId)}
								aria-label={t("mc.multi.grid.remove.facility", { facility: facility.departmentName })}
								data-testid={`mc-multi-performance-remove-${facility.departmentId}`}
								sx={{ opacity: 0, transition: "opacity 120ms", ".MuiTableRow-root:hover &, &:focus-visible": { opacity: 1 } }}
							>
								<CloseIcon sx={{ fontSize: 14 }} />
							</IconButton>
						</Tooltip>
					</Box>
				),
				ariaLabel: facility.departmentName,
				cells: Object.fromEntries(
					data.weeks.map((week, index) => {
						const value = values[index];
						if (value === undefined)
							throw new Error(`Performance response has no ${effectiveMetric} value for ${facility.departmentId} week ${week.startDate}`);
						const displayValue = displayCellValue(value, displayMode);
						const variance = performanceVariance(value);
						const distance = performanceDistanceFromTarget(value);
						const weekRange = formatWeekRange(week.startDate, week.endDate, i18n.language);
						const actualLabel = t(isActual ? "mc.multi.performance.tooltip.actual" : "mc.multi.performance.tooltip.scheduled", {
							value: value.actual === null ? t("mc.multi.performance.no.data") : formatPerformanceNumber(value.actual, effectiveMetric, i18n.language),
						});
						const targetLabel = t("mc.multi.performance.tooltip.target", {
							value: value.target === null ? t("mc.multi.performance.no.data") : formatPerformanceNumber(value.target, effectiveMetric, i18n.language),
						});
						const varianceLabel = t("mc.multi.performance.tooltip.variance", {
							value: variance === null ? t("mc.multi.performance.no.data") : formatPerformanceVariance(variance, effectiveMetric, i18n.language),
						});
						const distanceLabel = t("mc.multi.performance.tooltip.distance", {
							value: distance === null ? t("mc.multi.performance.no.data") : formatPerformanceDistance(distance, i18n.language),
						});
						return [
							week.startDate,
							{
								value:
									displayValue === null
										? t("mc.multi.performance.no.data")
										: displayMode === "difference"
											? formatPerformanceVariance(displayValue, effectiveMetric, i18n.language)
											: formatPerformanceNumber(displayValue, effectiveMetric, i18n.language),
								tone: performanceCellTone(value),
								tooltip: (
									<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
										<Typography variant="caption">{facility.departmentName}</Typography>
										<Typography variant="caption">{weekRange}</Typography>
										<Typography variant="caption">{actualLabel}</Typography>
										<Typography variant="caption">{targetLabel}</Typography>
										<Typography variant="caption">{varianceLabel}</Typography>
										<Typography variant="caption">{distanceLabel}</Typography>
									</Box>
								),
								ariaLabel: `${facility.departmentName}: ${weekRange}. ${actualLabel}. ${targetLabel}. ${varianceLabel}. ${distanceLabel}`,
								dataTestId: `mc-multi-performance-cell-${facility.departmentId}-${week.startDate}`,
							},
						];
					}),
				),
			};
		});
	}, [currentDepartmentId, data, displayMode, effectiveMetric, facilities, i18n.language, isActual, t, onRemoveFacility]);

	/**
	 * Network average, pinned as the last row regardless of the chosen sort — a summary belongs at the
	 * bottom, not shuffled among the facilities it summarizes.
	 *
	 * Wage-restricted facilities are excluded from the PPD average: their cells are zeroes standing in
	 * for data the reader may not see, and averaging those in would pull the network figure toward zero.
	 */
	const averageRow = useMemo<PerformanceHeatmapRow | undefined>(() => {
		if (data === undefined || facilities.length === 0 || data.weeks.length === 0) return undefined;
		const excluded = effectiveMetric === "ppd" ? wageRestrictedDepartmentIds : new Set<string>();
		const contributing = facilities.filter((facility) => !excluded.has(facility.departmentId)).length;
		// Every facility on screen is restricted for this metric: an average of nothing is not a number.
		if (contributing === 0) return undefined;
		const values = performanceAverageRow(facilities, effectiveMetric, data.weeks.length, excluded);
		return {
			id: AVERAGE_ROW_ID,
			label: (
				<Box component="span" sx={{ fontWeight: 700 }}>
					{t("mc.multi.performance.average", { count: contributing })}
				</Box>
			),
			ariaLabel: t("mc.multi.performance.average", { count: contributing }),
			cells: Object.fromEntries(
				data.weeks.map((week, index) => {
					const value = values[index];
					if (value === undefined) throw new Error(`Average row has no value for week ${week.startDate}`);
					const displayValue = displayCellValue(value, displayMode);
					const variance = performanceVariance(value);
					const weekRange = formatWeekRange(week.startDate, week.endDate, i18n.language);
					const actualLabel = t(isActual ? "mc.multi.performance.tooltip.actual" : "mc.multi.performance.tooltip.scheduled", {
						value: value.actual === null ? t("mc.multi.performance.no.data") : formatPerformanceNumber(value.actual, effectiveMetric, i18n.language),
					});
					const targetLabel = t("mc.multi.performance.tooltip.target", {
						value: value.target === null ? t("mc.multi.performance.no.data") : formatPerformanceNumber(value.target, effectiveMetric, i18n.language),
					});
					const varianceLabel = t("mc.multi.performance.tooltip.variance", {
						value: variance === null ? t("mc.multi.performance.no.data") : formatPerformanceVariance(variance, effectiveMetric, i18n.language),
					});
					return [
						week.startDate,
						{
							// Same branch as the facility cells: Difference mode must keep its sign.
							value:
								displayValue === null
									? t("mc.multi.performance.no.data")
									: displayMode === "difference"
										? formatPerformanceVariance(displayValue, effectiveMetric, i18n.language)
										: formatPerformanceNumber(displayValue, effectiveMetric, i18n.language),
							// Deliberately tone-free: the summary row is a reference line, and coloring it
							// against target would compete with the facility rows it is there to compare.
							tone: "neutral" as const,
							tooltip: (
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
									<Typography variant="caption" sx={{ fontWeight: 700 }}>
										{t("mc.multi.performance.average", { count: contributing })}
									</Typography>
									<Typography variant="caption">{actualLabel}</Typography>
									<Typography variant="caption">{targetLabel}</Typography>
									<Typography variant="caption">{varianceLabel}</Typography>
								</Box>
							),
							ariaLabel: `${t("mc.multi.performance.average", { count: contributing })}: ${weekRange}. ${actualLabel}. ${targetLabel}. ${varianceLabel}.`,
							dataTestId: `mc-multi-performance-average-cell-${week.startDate}`,
						},
					];
				}),
			),
		};
	}, [data, facilities, effectiveMetric, wageRestrictedDepartmentIds, displayMode, i18n.language, isActual, t]);

	const rowsWithAverage = useMemo(() => (averageRow ? [...heatmapRows, averageRow] : heatmapRows), [heatmapRows, averageRow]);
	const heatmapColumns = useMemo(
		() =>
			data === undefined
				? []
				: data.weeks.map((week) => ({
						id: week.startDate,
						label: formatWeekRange(week.startDate, week.endDate, i18n.language),
						ariaLabel: formatWeekRange(week.startDate, week.endDate, i18n.language),
					})),
		[data, i18n.language],
	);

	return (
		<CommonDashboardBox
			data-testid="mc-multi-performance-heatmap"
			sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 1.5, width: "100%" }}
		>
			<CommonDashboardHeader label={t("mc.multi.performance.title")} />
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
				<Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
					<SystemFlatSwitch<PerformanceMetric>
						options={metricOptions}
						activeIndexOrValue={effectiveMetric}
						onSwitch={(option) => {
							setMetric(option.value);
							track("management_center_multi_performance_metric_changed", { metric: option.value });
						}}
						delayCallback={0}
						data-testid="mc-multi-performance-metric-toggle"
					/>
					<SystemFlatSwitch<PerformanceDisplayMode>
						options={displayOptions}
						activeIndexOrValue={displayMode}
						onSwitch={(option) => {
							setDisplayMode(option.value);
							track("management_center_multi_performance_display_changed", { display_mode: option.value });
						}}
						delayCallback={0}
						data-testid="mc-multi-performance-display-toggle"
					/>
				</Box>
				<SystemDropdown<DropdownItem<PerformanceSort>>
					items={sortItems}
					selectedItem={sort}
					onChange={(nextSort) => {
						if (nextSort === undefined) return;
						setSort(nextSort);
						track("management_center_multi_performance_sort_changed", { sort: nextSort });
					}}
					label={t("mc.multi.performance.sort.label")}
					data-testid="mc-multi-performance-sort"
					formControlSx={{ width: { xs: "100%", sm: "auto" } }}
				/>
			</Box>
			{isLoading ? (
				<Box data-testid="mc-multi-performance-loading" sx={{ py: { xs: 6, lg: 8 }, display: "flex", alignItems: "center", justifyContent: "center" }}>
					<JumpingDots />
				</Box>
			) : isError ? (
				<EmptyState
					dataTestId="mc-multi-performance-error"
					icon={<InsightsOutlinedIcon />}
					title={t("mc.multi.performance.error.title")}
					hint={t("mc.multi.performance.error.hint")}
				/>
			) : data !== undefined && facilities.length === 0 ? (
				<EmptyState
					dataTestId="mc-multi-performance-empty"
					icon={<InsightsOutlinedIcon />}
					title={t("mc.multi.performance.empty.title")}
					hint={t("mc.multi.performance.empty.hint")}
				/>
			) : data !== undefined ? (
				<InHousePerformanceHeatmap
					columns={heatmapColumns}
					rows={rowsWithAverage}
					ariaLabel={t("mc.multi.performance.title")}
					rowHeaderLabel={t("mc.multi.performance.facility")}
					stickyHeader
					stickyFirstColumn
					dataTestId="mc-multi-performance-heatmap-table"
				/>
			) : null}
		</CommonDashboardBox>
	);
};
